<template>
  <div>
    <b-modal
      id="product-edit-modal"
      size="xl"
      centered
      hide-header-close
      hide-header
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
      dialog-class="product-edit-modal"
      content-class="product-edit-content"
      body-class="product-body"
      @hidden="handleHide"
    >
      <div class="product-edit-container">
        <div class="d-flex w-100 justify-content-between padding-24 pb-0">
          <h1 class="page-title">
            Product Editing
          </h1>
          <span @click="handleClose">
            <close-icon class="close-icon" />
          </span>
        </div>
        <ValidationObserver v-slot="{ invalid }">
          <div class="top-section">
            <div class="main-row">
              <images-section />
              <div class="input-section-right-container">
                <basic-information />
                <pricing ref="pricingComponent" />
              </div>
            </div>
            <product-inventory />
            <additional-info />
          </div>
          <div class="bottom-section">
            <b-button
              variant="light"
              class="modal-btn close-modal-btn"
              @click="handleClose"
            >
              Close
            </b-button>
            <b-button
              variant="light"
              class="modal-btn save-modal-btn"
              :disabled="isSaveDisabled || invalid"
              @click="handleSave"
            >
              {{ isSaving ? 'Saving...' : (isSaved ? 'Saved' : 'Save') }}
            </b-button>
          </div>
        </ValidationObserver>
      </div>
    </b-modal>
    <confirmation-modal
      v-if="isCloseModalVisible"
      :is-modal-visible="isCloseModalVisible"
      @ok="handleCloseConfirm"
      @hide="handleConfirmHide"
    />

    <!-- Custom success toast -->
    <transition name="fade">
      <div
        v-if="showSuccessToast"
        class="custom-success-toast"
      >
        <div class="success-notification-content">
          <feather-icon
            icon="CheckIcon"
            size="24"
            class="success-check-icon"
          />
          <span>
            {{ updatedPricesText }} for {{ updatedOrdersCount }} selected orders were updated successfully!
          </span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import CloseIcon from '@/@core/assets/svg-components/CloseIcon.vue'
import {
  BModal, BButton,
} from 'bootstrap-vue'
import ProductInventory from '@/views/apps/product/edit/inventory-and-moq/ProductInventory.vue'
import {
  CHECK_PRODUCT_ORDERS,
  RESET_EDIT_STORE,
  UPDATE_PRODUCT,
  UPLOAD_NEW_PRODUCT_IMAGE,
} from '@/store/modules/productEdit.module'
import store from '@/store'
import { ValidationObserver } from 'vee-validate'
import { apiToastError, apiToastSuccess } from '@/@core/utils/toast'
import analytics from '@/@core/utils/analytics'
import constants from '@/constants'
import ImagesSection from './images-section/ImagesSection.vue'
import BasicInformation from './input-sections/BasicInformation.vue'
import Pricing from './input-sections/Pricing.vue'
import AdditionalInfo from './input-sections/AdditionalInfo.vue'
import ConfirmationModal from './ConfirmationModal.vue'

export default {
  name: 'ProductEditModal',
  components: {
    CloseIcon,
    ImagesSection,
    BasicInformation,
    Pricing,
    ProductInventory,
    AdditionalInfo,
    BModal,
    BButton,
    ValidationObserver,
    ConfirmationModal,
  },
  data() {
    return {
      productInfo: {},
      imageIsValid: store.state.productEdit.productImages.length > 0,
      isSaving: false,
      isSaved: false,
      isCloseModalVisible: false,
      updatedOrdersCount: 0,
      showSuccessToast: false,
      updatedPricesText: '',
    }
  },
  computed: {
    changedFields() {
      return store.state.productEdit.changedFields || []
    },
    isSaveDisabled() {
      if (this.isSaved) return true
      return this.changedFields.length === 0 || this.isSaving || !this.areImagesValid
    },
    areImagesValid() {
      return store.state.productEdit.productImages.length > 0
    },
  },
  watch: {
    changedFields(newValue) {
      if (newValue.length > 0 && this.isSaved) {
        this.isSaved = false
      }
    },
  },
  beforeDestroy() {
    store.commit(RESET_EDIT_STORE)
  },
  methods: {
    handleConfirmHide() {
      this.isCloseModalVisible = false
      analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_CLOSES_CONFIRMATION_MODAL, {
        productId: store.state.productEdit.savedState._id,
        collectionId: store.state.productEdit.savedState.collectionId,
        formData: store.state.productEdit.newData,
      })
    },
    handleCloseConfirm() {
      analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_CLICKS_OK_ON_CONFIRMATION_MODAL, {
        productId: store.state.productEdit.savedState._id,
        collectionId: store.state.productEdit.savedState.collectionId,
        formData: store.state.productEdit.newData,
      })
      this.isCloseModalVisible = false
      this.$bvModal.hide('product-edit-modal')
    },
    handleHide() {
      analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_CLOSES_PRODUCT_EDIT_MODAL, {
        product: store.state.productEdit.savedState,
      })
    },
    handleClose() {
      analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_CLICKS_CLOSE_BUTTON, {
        product: store.state.productEdit.savedState,
      })
      if (this.changedFields.length > 0) {
        analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_VIEWS_CONFIRMATION_MODAL, {
          productId: store.state.productEdit.savedState._id,
          collectionId: store.state.productEdit.savedState.collectionId,
          formData: store.state.productEdit.newData,
        })
        this.isCloseModalVisible = true
      } else {
        this.$bvModal.hide('product-edit-modal')
      }
    },
    async uploadLocalImages() {
      const productImages = store.state.productEdit.productImages
      const areLocalImagesPresent = productImages.some(img => img.isLocal)
      let imagesUploadSuccess = true
      if (areLocalImagesPresent) {
        for await (const image of productImages) {
          if (image.isLocal) {
            const formData = new FormData()
            formData.append('file', image.file)

            try {
              const uploadUrl = await store.dispatch(UPLOAD_NEW_PRODUCT_IMAGE, {
                formData,
                index: image.index,
              })

              if (!uploadUrl) {
                throw new Error('Failed to upload one or more images.')
              } else {
                image.isLocal = false
                analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_UPLOAD_IMAGE_SUCCESS, {
                  productId: store.state.productEdit.savedState._id,
                  collectionId: store.state.productEdit.savedState.collectionId,
                  imageIndex: image.index,
                  imageUrl: uploadUrl,
                })
              }
            } catch (error) {
              analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_UPLOAD_IMAGE_FAILED, {
                productId: store.state.productEdit.savedState._id,
                collectionId: store.state.productEdit.savedState.collectionId,
                imageIndex: image.index,
                imageUrl: image.url,
                error: 'Failed to upload one or more images.',
              })
              imagesUploadSuccess = false
            }
          }
        }
      }
      return imagesUploadSuccess
    },
    handleSuccessfulSave() {
      this.isSaved = true
      this.showSuccessSaveToast()
      this.closeProductEditModal()
    },
    async _saveProductAndUpdateOrders() {
      try {
        const response = await store.dispatch(UPDATE_PRODUCT)

        const pricingComponent = this.$refs.pricingComponent
        if (pricingComponent?.priceChanged) {
          const productId = store.state.productEdit.savedState._id
          const hasOrders = await store.dispatch(CHECK_PRODUCT_ORDERS, { productId })

          if (hasOrders) {
            this.handlePriceChange(pricingComponent, response)
          } else {
            this.handleSuccessfulSave()
          }
        } else {
          this.handleSuccessfulSave()
        }
      } catch (error) {
        apiToastError(error || 'Failed to update product.')
        analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_SAVE_FAILED, {
          productId: store.state.productEdit.savedState._id,
          collectionId: store.state.productEdit.savedState.collectionId,
          payload: store.state.productEdit.newData,
          error,
        })
      }
    },
    hasItemsMissingColorData() {
      const items = store.state.productEdit.newData?.items || []
      return items.some(item => !item.colorFamily || !item.hexCode)
    },
    async handleSave() {
      analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_CLICKS_SAVE_BUTTON, {
        productId: store.state.productEdit.savedState._id,
        collectionId: store.state.productEdit.savedState.collectionId,
        formData: store.state.productEdit.newData,
      })
      if (!this.areImagesValid) {
        apiToastError('Main image is required.')
        return
      }

      if (this.hasItemsMissingColorData()) {
        const confirmed = await this.$bvModal.msgBoxConfirm(
          'Some items are missing Color Family or Hex Code. Do you still want to continue?',
          {
            title: 'Missing Color Data',
            okTitle: 'Yes, Save',
            cancelTitle: 'Cancel',
            centered: true,
          },
        )
        if (!confirmed) return
      }

      try {
        document.activeElement.blur()
        this.isSaving = true
        const imagesUploadSuccess = await this.uploadLocalImages()
        if (!imagesUploadSuccess) {
          apiToastError('Failed to upload one or more images.')
          return
        }
        await this._saveProductAndUpdateOrders()
      } finally {
        this.isSaving = false
      }
    },
    handlePriceChange(pricingComponent, response) {
      pricingComponent.showPriceUpdateWarning = true
      this.showSuccessSaveToast(response)
      this.$root.$once('orders-price-updated', this.handleOrdersPriceUpdated)
      this.$root.$once('price-update-closed', this.closeProductEditModal)

      this.isSaved = true
    },
    handleOrdersPriceUpdated({ count, prices }) {
      this.updatedOrdersCount = count

      const parts = []
      if (prices.includes('Wholesale')) parts.push('Wholesale')
      if (prices.includes('Retail')) parts.push('Retail')

      this.updatedPricesText = parts.length
        ? `${parts.join(' and ')} prices`
        : 'Prices'

      this.showSuccessToast = count

      setTimeout(() => {
        this.showSuccessToast = false
      }, 2000)
    },
    showSuccessSaveToast(response) {
      analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_SAVE_SUCCESS, {
        productId: store.state.productEdit.savedState._id,
        collectionId: store.state.productEdit.savedState.collectionId,
        payload: store.state.productEdit.newData,
        response,
      })
      apiToastSuccess('Product updated successfully!')
      this.$emit('edit-success')
    },
    closeProductEditModal() {
      setTimeout(() => this.$bvModal.hide('product-edit-modal'), 500)
    },
  },
}
</script>

<style lang="scss">
@import '~@core/scss/base/bootstrap-extended/include'; // Bootstrap includes
@import './common-styles.scss';

// Custom toast styling
.custom-success-toast {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  border-radius: 8px;
  background: #292929;
  color: #FFF;
  font-feature-settings: 'liga' off, 'clig' off;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  .success-notification-content {
    display: flex;
    padding: 12px 16px;
    align-items: center;
    gap: 24px;

    .success-check-icon {
      color: #FFF;
      stroke-width: 2;
    }
  }
}

// Fade animation
.fade-enter-active, .fade-leave-active {
  transition: opacity .5s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

.product-edit-modal {
  width: calc(100vw - 96px);
  height: calc(100vh - 96px);
}

.product-edit-content {
  width: 100%;
  height: 100%;
}

.product-body {
  padding: 0;
  max-height: 100%;
  max-width: 100%;
}

.product-edit-container {
  border-radius: 8px;
  background-color: #fff;
  width: 100%;
  height: 100%;
}

.page-title {
  font-size: 20px;
  font-weight: 500;
  margin-bottom: 12px;
}

.close-icon {
  cursor: pointer;
  transform: scale(1.2);
  transition: all 0.2s ease-in-out;

  &:hover {
    transform: scale(1.4) translateY(-2px);
  }
}

.input-section-right-container {
  width: calc(100% - 660px);
}

.main-row {
  display: flex;
  gap: 24px;
}

.main-row>div {
  height: 100%;
}

.top-section {
  height: calc(100% - 129px);
  overflow: scroll;
  padding: 24px;
  padding-top: 0;
}

.bottom-section {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding: 16px;
  height: 69px;
  border-top: 1px solid #E0E0E0;
}

.modal-btn {
  display: flex;
  width: 116px;
  height: 37px;
  padding: 10px 12px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 5px;
  font-size: 14px;
}

.close-modal-btn {
  background: #F5F5F5;
}

.save-modal-btn {
  background: #EDE1F5;

  &:hover,
  &:active,
  &:focus {
    background: #EDE1F5;
  }
}
</style>
